<script>
import {
  ValidationObserver,
  ValidationProvider,
  localize,
  extend,
} from "vee-validate";
import * as rules from "vee-validate/dist/rules";
import ja from "vee-validate/dist/locale/ja";
import axios from "axios";

// ルール設定
for (let rule in rules) {
  extend(rule, rules[rule]);
}
// 日本語化
localize("ja", ja);

// 英字・数字混在のカスタムルール追加
extend("alpha_num_mix", {
  validate: (value) => {
    return /[a-zA-Z]/.test(value) && /\d/.test(value);
  },
  message: "パスワードは英字と数字を両方含めてください",
});

export default {
  components: {
    ValidationObserver,
    ValidationProvider,
  },
  data() {
    return {
      name: "",
      email: "",
      password: "",
      password_confirmation: "",
      isRegisterError: false,
      registerSuccess: false,
      regError: "",
    };
  },
  mounted() {
    // URLパラメータからtokenを取得
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");

    if (token) {
      // APIリクエストでtokenに対応するemailを取得
      axios
        .get(`/api/users/token`, { params: { token } })
        .then((response) => {
          this.email = response.data.email; // emailを設定
        })
        .catch((error) => {
          console.error("Error fetching email:", error);
          this.isRegisterError = true;
          this.regError = "トークンが無効です"; // エラーメッセージを設定
        });
    } else {
      console.error("Token not found in URL");
      this.isRegisterError = true;
      this.regError = "トークンが無効です"; // エラーメッセージを設定
    }
  },
  computed: {
    rules() {
      return {
        required: true,
        min: 8,
        confirmed: "confirm",
        alpha_num_mix: true, // 追加
      };
    },
  },
  methods: {
    async handleRegister(event) {
      event.preventDefault(); // デフォルトのフォーム送信を防止
      this.isRegisterError = false;
      this.registerSuccess = false;

      try {
        // APIリクエストを送信
        const response = await axios.post("/api/users/register", {
          name: this.name,
          email: this.email,
          password: this.password,
          password_confirmation: this.password_confirmation,
        });

        // ユーザー登録成功
        this.registerSuccess = true;
        console.log("User registered successfully:", response.data);

        // 入力欄を空にする
        this.email = "";

        // 5秒待機してログイン画面へリダイレクト
        setTimeout(() => {
          window.location.href = "/login"; // ログイン画面のURLにリダイレクト
        }, 3000);
      } catch (error) {
        // エラーハンドリング
        this.isRegisterError = true;
        if (error.response && error.response.data && error.response.data.message) {
          this.regError = error.response.data.message;
        } else if (error.response && error.response.data && error.response.data.errors) {
          // バリデーションエラーの場合
          this.regError = Object.values(error.response.data.errors)
            .flat()
            .join(", ");
        } else {
          this.regError = "ユーザー登録中にエラーが発生しました。";
        }
        console.error("Error during registration:", error);
      }
    },
  },
};
</script>

<template>
  <div>
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-md-8 col-lg-6 col-xl-5">
          <div class="card overflow-hidden mt-2">
            <div class="card-body pt-0">
              <!-- Success Alert -->
              <b-alert v-model="registerSuccess" class="mt-3" variant="success" dismissible>
                Registration successful.
              </b-alert>

              <!-- Error Alert -->
              <b-alert v-model="isRegisterError" class="mt-3" variant="danger" dismissible>
                {{ regError }}
              </b-alert>
              <h3 class="ps-2 pt-2 mt-4">Register account</h3>
              <!-- Registration Form -->
              <ValidationObserver v-slot="{ invalid }">
                <b-form class="p-2 mt-3" @submit="handleRegister" autocomplete="off">
                  <!-- Name -->
                  <b-form-group label="Name" label-for="name" class="mb-3">
                    <ValidationProvider name="Name" rules="required" v-slot="{ errors }">
                      <b-form-input id="name" v-model="name" name="name" type="text" placeholder="Enter your name"
                        autocomplete="off"></b-form-input>
                      <p class="text-danger">{{ errors[0] }}</p>
                    </ValidationProvider>
                  </b-form-group>

                  <!-- Email -->
                  <b-form-group label="Email" label-for="email" class="mb-3">
                    <ValidationProvider name="Email" rules="required" v-slot="{ errors }">
                      <b-form-input id="email" v-model="email" name="email" type="email"
                        placeholder="Email is not editable" autocomplete="off" disabled></b-form-input>
                      <p class="text-danger">{{ errors[0] }}</p>
                    </ValidationProvider>
                  </b-form-group>

                  <!-- Password -->
                  <b-form-group label="Password" label-for="password" class="mb-3">
                    <ValidationProvider name="Password" :rules="rules" v-slot="{ errors }">
                      <b-form-input id="password" v-model="password" name="password" type="password"
                        placeholder="Enter your password" autocomplete="new-password"></b-form-input>
                      <p class="text-danger">{{ errors[0] }}</p>
                    </ValidationProvider>
                  </b-form-group>

                  <!-- Confirm Password -->
                  <b-form-group label="Confirm Password" label-for="password-confirm" class="mb-3">
                    <ValidationProvider name="Confirm Password" rules="" vid="confirm" v-slot="{ errors }">
                      <b-form-input id="password-confirm" v-model="password_confirmation" name="password_confirmation"
                        type="password" placeholder="Confirm your password" autocomplete="off"></b-form-input>
                      <p class="text-danger">{{ errors[0] }}</p>
                    </ValidationProvider>
                  </b-form-group>

                  <div class="form-group row mb-0">
                    <div class="col-12 text-end">
                      <button class="btn btn-primary w-md" type="submit" :disabled="invalid">Register</button>
                    </div>
                  </div>
                </b-form>
              </ValidationObserver>
            </div>
          </div>
        </div>
        <div class="mt-4 text-center">
          <p>© {{ new Date().getFullYear() }} Avalon Technologies, Inc.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.auth-page {
  .card {
    border: none;
    box-shadow: 0 0 13px rgba(0, 0, 0, 0.05);
  }

  .text-primary {
    color: #556ee6 !important;
  }

  .fw-medium {
    font-weight: 500;
  }

  .text-muted {
    color: #6c757d !important;
  }

  .btn-primary {
    background-color: #556ee6;
    border-color: #556ee6;
  }

  .btn-primary:hover {
    background-color: #4458c5;
    border-color: #4458c5;
  }
}
</style>
